import { useId, useState } from 'react';
import { useFormState, useWatch } from 'react-hook-form';
import type { Control, FieldError, FieldErrors, UseFormSetValue } from 'react-hook-form';
import { IconInfoCircle, IconMapPin } from '@tabler/icons-react';
import { FormField } from './FormField';
import { FormAddressAutocompleteField } from './FormAddressAutocompleteField';
import type { AddressAutocompleteState, AddressDetails, AddressFormValues } from '../../types/address.types';
import { pl } from '../../messages/pl';

const messages = pl.addressForm;

type AddressFieldName = 'street' | 'postalCode' | 'city';

interface AddressFieldConfig {
  name: AddressFieldName;
  label: string;
  placeholder: string;
  columnClassName: string;
}

const ADDRESS_FIELDS: readonly AddressFieldConfig[] = [
  { name: 'street', label: messages.streetLabel, placeholder: messages.streetPlaceholder, columnClassName: 'col-md-6' },
  { name: 'postalCode', label: messages.postalCodeLabel, placeholder: messages.postalCodePlaceholder, columnClassName: 'col-md-3' },
  { name: 'city', label: messages.cityLabel, placeholder: messages.cityPlaceholder, columnClassName: 'col-md-3' },
];

const ADDRESS_FIELD_NAMES = ADDRESS_FIELDS.map((field) => field.name);

interface FormAddressSectionProps<T extends AddressFormValues> {
  control: Control<T>;
  setValue: UseFormSetValue<T>;
  autocompleteState: AddressAutocompleteState;
  onInputChange: (input: string) => void;
  onSelectSuggestion: (placeId: string) => Promise<AddressDetails | null>;
  error?: FieldError;
  defaultValue?: string;
  label?: string;
  required?: boolean;
}

function formatAddressSummary(street?: string, postalCode?: string, city?: string): string {
  const locality = [postalCode, city].filter(Boolean).join(' ');

  return [street, locality].filter(Boolean).join(', ');
}

function getToggleLabel(isExpanded: boolean, hasAddress: boolean): string {
  if (isExpanded) return messages.collapseDetails;

  return hasAddress ? messages.editManually : messages.enterManually;
}

export function FormAddressSection<T extends AddressFormValues>({
  control,
  setValue,
  autocompleteState,
  onInputChange,
  onSelectSuggestion,
  error,
  defaultValue = '',
  label = messages.searchLabel,
  required = true,
}: FormAddressSectionProps<T>) {
  const addressControl = control as unknown as Control<AddressFormValues>;
  const setAddressValue = setValue as unknown as UseFormSetValue<AddressFormValues>;
  const detailsId = useId();
  const [isExpanded, setIsExpanded] = useState(false);
  const { errors } = useFormState({ control: addressControl, name: ADDRESS_FIELD_NAMES });
  const [street, postalCode, city] = useWatch({ control: addressControl, name: ADDRESS_FIELD_NAMES });

  const hasAddressFieldError = ADDRESS_FIELD_NAMES.some((name) => errors[name]);
  if (hasAddressFieldError && !isExpanded) {
    setIsExpanded(true);
  }

  const addressSummary = formatAddressSummary(street, postalCode, city);
  const attachedCornersClassName = isExpanded ? 'rounded-bottom-0' : '';

  const clearCoordinates = () => {
    setAddressValue('latitude', undefined, { shouldDirty: true });
    setAddressValue('longitude', undefined, { shouldDirty: true });
  };

  return (
    <div className="row g-3">
      <div className="col-12">
        <FormAddressAutocompleteField<T>
          setValue={setValue}
          label={label}
          placeholder={messages.searchPlaceholder}
          required={required}
          error={hasAddressFieldError ? undefined : error}
          defaultValue={defaultValue}
          autocompleteState={autocompleteState}
          onInputChange={onInputChange}
          onSelectSuggestion={onSelectSuggestion}
          wrapperClassName="mb-0"
        />
      </div>
      <div className="col-12">
        <div className="form-label" id={`${detailsId}-label`}>
          {messages.detailsLabel}
        </div>
        <div className="input-group" role="group" aria-labelledby={`${detailsId}-label`}>
          <span className={`input-group-text bg-light ${attachedCornersClassName}`}>
            <IconMapPin size={18} />
          </span>
          <div className="form-control bg-light text-truncate" title={addressSummary || undefined}>
            {addressSummary || <span className="text-secondary">{messages.detailsEmpty}</span>}
          </div>
          <button
            type="button"
            className={`btn btn-primary ${attachedCornersClassName}`}
            aria-expanded={isExpanded}
            aria-controls={detailsId}
            disabled={hasAddressFieldError}
            onClick={() => setIsExpanded(!isExpanded)}>
            {getToggleLabel(isExpanded, Boolean(addressSummary))}
          </button>
        </div>
        {isExpanded && <AddressDetailsPanel id={detailsId} control={addressControl} errors={errors} onAddressInput={clearCoordinates} />}
      </div>
    </div>
  );
}

interface AddressDetailsPanelProps {
  id: string;
  control: Control<AddressFormValues>;
  errors: FieldErrors<AddressFormValues>;
  onAddressInput: () => void;
}

function AddressDetailsPanel({ id, control, errors, onAddressInput }: AddressDetailsPanelProps) {
  return (
    <div className="border border-top-0 rounded-bottom bg-light p-3" id={id}>
      <div className="row g-3">
        {ADDRESS_FIELDS.map((field) => (
          <div key={field.name} className={field.columnClassName}>
            <FormField
              control={control}
              name={field.name}
              label={field.label}
              placeholder={field.placeholder}
              required
              error={errors[field.name]}
              onInput={onAddressInput}
              wrapperClassName="mb-0"
            />
          </div>
        ))}
        <div className="col-12">
          <div className="text-secondary small d-flex align-items-start gap-1">
            <IconInfoCircle size={16} className="flex-shrink-0" />
            <span>{messages.manualEntryHint}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
